import { API_ERROR_MESSAGES } from "common/apiErrorHandling/apiErrorMessages";

type HttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE" | "DEL";

type MethodMessages = {
  [K in HttpMethod]?: string;
} & {
  default?: string; // Fallback if specific method not found
};

export type EndpointErrorConfig = {
  methods: MethodMessages;
  showNotification?: boolean; // defaults to true
  customErrorHandler?: <T>(err: any, context?: T) => boolean;
};

export type ErrorLookupResult = {
  message: string | undefined;
  showNotification: boolean;
  customErrorHandler?: <T>(err: any, context?: T) => boolean;
};

export const getErrorMessageForEndpoint = (
  url: string,
  method?: string
): ErrorLookupResult => {
  // Extract path from URL (remove query params)
  let path: string;
  try {
    const urlObj = new URL(url);
    path = urlObj.pathname;
  } catch {
    path = url.split("?")[0];
  }

  const upperMethod = method?.toUpperCase() as HttpMethod | undefined;

  // Strategy 1: Exact path match
  const exactConfig = API_ERROR_MESSAGES[path];
  if (exactConfig) {
    const message = upperMethod
      ? exactConfig.methods[upperMethod] || exactConfig.methods.default
      : exactConfig.methods.default;

    return {
      message,
      showNotification: exactConfig.showNotification ?? true,
      customErrorHandler: exactConfig.customErrorHandler
    };
  }

  // Strategy 2: Wildcard path match
  for (const [pattern, config] of Object.entries(API_ERROR_MESSAGES)) {
    if (matchesWildcard(path, pattern)) {
      const message = upperMethod
        ? config.methods[upperMethod] || config.methods.default
        : config.methods.default;

      return {
        message,
        showNotification: config.showNotification ?? true,
        customErrorHandler: config.customErrorHandler
      };
    }
  }

  // No match found - return undefined message but still show notification (fail-safe)
  return {
    message: undefined,
    showNotification: true
  };
};

const matchesWildcard = (path: string, pattern: string): boolean => {
  const regexPattern = pattern
    .split("/")
    .map((segment) => (segment === "*" ? "[^/]+" : escapeRegex(segment)))
    .join("/");

  const regex = new RegExp(`^${regexPattern}$`);
  return regex.test(path);
};

const escapeRegex = (str: string): string => {
  return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
};
